import { Body2, Body3, DividerLine, Tag } from '@cointracker/styleguide';
import { Grid } from '@cointracker/styleguide/src/Grid';
import { Select, SelectItem } from '@cointracker/styleguide/src/Select/Select';
import { SkeletonLine } from '@cointracker/styleguide/src/Skeleton';
import ArrowsConvertIcon from '@cointracker/styleguide/src/icons/arrows-convert.svg?react';
import { H5 } from '@cointracker/ui';
import { REBRAND_URLS } from '@cointracker/ui/src/utils/urls';
import { ArrowsLeftRightIcon, CoinsIcon } from '@phosphor-icons/react';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { useEffect } from 'react';
import { useBoundCalculatorStore } from '../../Calculator/store';
import type {
  CoinInfoType,
  CoinsMin1Length,
  CurrenciesMin1Length,
  CurrencyInfoType,
} from '../../Calculator/types';
import { CoinSelect } from './CoinSelect';
import { CurrencySelect } from './CurrencySelect';
import { STATE_TAX_RATES } from './TaxMath';
import {
  calculator,
  container,
  convertIcon,
  divider,
  inputGroup,
  resultDetail,
  resultDetailsContainer,
  resultGroup,
  resultTitle,
  selectGroup,
  selectTrigger,
  tag,
} from './TradeCalculator.css';
import { ValueInput } from './ValueInput';

export const TaxCalculator = ({
  coins: coinProps,
  currencies: currenciesProps,
}: {
  coins: CoinInfoType[];
  currencies: CurrencyInfoType[];
}) => {
  const {
    isInit,
    init,
    buyPrice,
    sellPrice,
    investment,
    investmentFee,
    exitFee,
    profitLoss,
    profitLossPercentage,
    afterTaxProfit,
    state,
    totalIncome,
    filingStatus,
    holdingPeriod,
    taxAmount,
    taxPercentage,
    tradeMode,
    totalCryptoGains,
    setBuyPrice,
    setSellPrice,
    setInvestment,
    setInvestmentFee,
    setExitFee,
    setState,
    setTotalIncome,
    setFilingStatus,
    setHoldingPeriod,
    setTradeMode,
    setTotalCryptoGains,
  } = useBoundCalculatorStore((state) => ({
    isInit: state.isInit,
    init: state.init,
    buyPrice: state.buyPrice,
    sellPrice: state.sellPrice,
    investment: state.investment,
    investmentFee: state.investmentFee,
    exitFee: state.exitFee,
    profitLoss: state.profitLoss,
    profitLossPercentage: state.profitLossPercentage,
    totalExitAmount: state.totalExitAmount,
    totalInvestmentAmount: state.totalInvestmentAmount,
    state: state.state,
    totalIncome: state.totalIncome,
    filingStatus: state.filingStatus,
    holdingPeriod: state.holdingPeriod,
    taxAmount: state.taxAmount,
    afterTaxProfit: state.afterTaxProfit,
    taxPercentage: state.taxPercentage,
    tradeMode: state.tradeMode,
    totalCryptoGains: state.totalCryptoGains,
    setBuyPrice: state.setBuyPrice,
    setSellPrice: state.setSellPrice,
    setInvestment: state.setInvestment,
    setInvestmentFee: state.setInvestmentFee,
    setExitFee: state.setExitFee,
    setState: state.setState,
    setTotalIncome: state.setTotalIncome,
    setFilingStatus: state.setFilingStatus,
    setHoldingPeriod: state.setHoldingPeriod,
    setTradeMode: state.setTradeMode,
    setTotalCryptoGains: state.setTotalCryptoGains,
  }));

  useEffect(() => {
    if (coinProps?.length > 0 && currenciesProps?.length > 0) {
      init(
        coinProps as CoinsMin1Length,
        currenciesProps as CurrenciesMin1Length,
      );
    }
  }, [coinProps, currenciesProps, init, isInit]);

  if (coinProps.length === 0 || currenciesProps.length === 0 || !isInit) {
    return <TaxCalculatorLoading />;
  }

  return (
    <Grid className={container}>
      <div className={calculator}>
        <div className="flex flex-col gap-15">
          <H5 as="h1">2025 Crypto Tax Calculator</H5>
          <Body2>Calculate your taxes</Body2>
        </div>

        <RadioGroup.Root
          value={tradeMode}
          onValueChange={(value) =>
            setTradeMode(value as 'single' | 'multiple')
          }
          className="flex w-full max-w-2xl gap-8"
        >
          <RadioGroup.Item
            value="single"
            className="flex h-104 flex-1 cursor-pointer items-center justify-center rounded-8 border border-line-card data-[state=checked]:bg-accent-black data-[state=checked]:text-text-primary-foreground-inverse data-[state=checked]:shadow-sm"
          >
            <div className="flex items-center gap-8">
              <ArrowsLeftRightIcon width={24} height={24} />
              <span>Single trade</span>
            </div>
          </RadioGroup.Item>
          <RadioGroup.Item
            value="multiple"
            className="flex h-104 flex-1 cursor-pointer items-center justify-center rounded-8 border border-line-card data-[state=checked]:bg-accent-black data-[state=checked]:text-text-primary-foreground-inverse data-[state=checked]:shadow-sm"
          >
            <div className="flex items-center gap-8">
              <CoinsIcon width={24} height={24} />
              <span>Multiple trades</span>
            </div>
          </RadioGroup.Item>
        </RadioGroup.Root>

        <div className={selectGroup}>
          <CurrencySelect />
          <ArrowsConvertIcon className={convertIcon} width={24} height={24} />
          <CoinSelect />
        </div>

        <div
          className="flex max-w-2xl flex-col gap-16 data-[trade-mode=single]:hidden"
          data-trade-mode={tradeMode}
        >
          <Body3>
            <b>
              Total Crypto Gains is your complete profit/loss from recent
              transactions.
            </b>{' '}
            If you manage multiple accounts, use different platforms, or engage
            in complex activities like DeFi, staking, or NFTs, a specialized
            tracking tool like CoinTracker can simplify your calculations.{' '}
            <a
              href={REBRAND_URLS.GET_STARTED}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Get started now
            </a>
            .
          </Body3>
        </div>
        <div className={inputGroup}>
          {tradeMode === 'single' ? (
            <>
              <ValueInput
                label="Investment"
                value={investment}
                action={setInvestment}
              />
              <ValueInput
                label="Buy price"
                value={buyPrice}
                action={setBuyPrice}
              />
              <ValueInput
                label="Sell price"
                value={sellPrice}
                action={setSellPrice}
              />
              <ValueInput
                label="Investment fee"
                value={investmentFee}
                action={setInvestmentFee}
              />
              <ValueInput
                label="Exit fee"
                value={exitFee}
                action={setExitFee}
              />
            </>
          ) : (
            <ValueInput
              label="Total Crypto Gains"
              value={totalCryptoGains}
              action={setTotalCryptoGains}
            />
          )}

          <ValueInput
            label="Total Income"
            value={totalIncome}
            action={setTotalIncome}
          />
          <Select
            value={state || 'none'}
            onValueChange={setState}
            className={selectTrigger}
          >
            <SelectItem value="none">Select State</SelectItem>
            {Object.keys(STATE_TAX_RATES).map((stateCode) => (
              <SelectItem key={stateCode} value={stateCode}>
                {stateCode}
              </SelectItem>
            ))}
          </Select>

          <Select
            className={selectTrigger}
            value={filingStatus}
            onValueChange={(value) =>
              setFilingStatus(value as 'single' | 'married')
            }
          >
            <SelectItem value="single">Single</SelectItem>
            <SelectItem value="married">Married</SelectItem>
          </Select>

          <Select
            className={selectTrigger}
            value={holdingPeriod}
            onValueChange={(value) =>
              setHoldingPeriod(value as 'short' | 'long')
            }
          >
            <SelectItem value="short">Short Term</SelectItem>
            <SelectItem value="long">Long Term</SelectItem>
          </Select>
        </div>
        <DividerLine className={divider} />
        <div className={resultGroup}>
          <H5 className={resultTitle}>Investment results</H5>

          <div className={resultDetailsContainer}>
            <div className={resultDetail}>
              <Body2>Profit / Loss</Body2>
              <Tag
                className={tag}
              >{`${profitLoss} (${profitLossPercentage}%)`}</Tag>
            </div>
            <div className={resultDetail}>
              <Body2>Capital Gains Tax</Body2>
              <Tag className={tag}>{`${taxAmount} (${taxPercentage}%)`}</Tag>
            </div>
            <div className={resultDetail}>
              <Body2>Profit / Loss After Taxes</Body2>
              <Tag className={tag}>{afterTaxProfit}</Tag>
            </div>
          </div>
        </div>
      </div>
    </Grid>
  );
};

export const TaxCalculatorLoading = () => {
  return (
    <Grid className={container}>
      <div className={calculator}>
        <div className={selectGroup}>
          <CurrencySelect loading />
          <SkeletonLine width={24} height={24} />
          <CoinSelect loading />
        </div>

        <div className={inputGroup}>
          <SkeletonLine height={40} />
          <SkeletonLine height={40} />
          <SkeletonLine height={40} />
          <SkeletonLine height={40} />
          <SkeletonLine height={40} />
          <SkeletonLine height={40} />
          <SkeletonLine height={40} />
          <SkeletonLine height={40} />
          <SkeletonLine height={40} />
        </div>
        <DividerLine className={divider} />
        <div className={resultGroup}>
          <SkeletonLine height={28} />

          <div className={resultDetailsContainer}>
            <div className={resultDetail}>
              <SkeletonLine height={20} width={102} />
              <SkeletonLine height={20} width={102} />
            </div>

            <div className={resultDetail}>
              <SkeletonLine height={20} width={176} />
              <SkeletonLine height={20} width={52} />
            </div>

            <div className={resultDetail}>
              <SkeletonLine height={20} width={120} />
              <SkeletonLine height={20} width={52} />
            </div>

            <div className={resultDetail}>
              <SkeletonLine height={20} width={100} />
              <SkeletonLine height={20} width={52} />
            </div>

            <div className={resultDetail}>
              <SkeletonLine height={20} width={140} />
              <SkeletonLine height={20} width={52} />
            </div>
          </div>
        </div>
      </div>
    </Grid>
  );
};
